import React, {useState, useCallback} from "react";
import {FilterOutlined} from '@ant-design/icons';
import {FILTER_TYPES} from "../menu/MenuCards";

import "../../css/filterBar.css";
import {useTheme} from "../header/Header";

// Компонент FilterBar (без изменений)
export function FilterBar({
                              showFilterPanel,
                              visibleContextCategories,
                              handleCategoryNavigation,
                              allTags,
                              selectedTags,
                              toggleTagFilter,
                              setSelectedTags,
                              availableProperties,
                              visiblePropertyValues,
                              propertyFilters,
                              updatePropertyFilter,
                              resetPropertyFilters,
                              isMobileFilterOpen,
                              onMobileFilterToggle,
                              onMobileFilterClose
                          }) {
    const [isTagsListCollapsed, setIsTagsListCollapsed] = useState(false);
    const [isPropertiesListCollapsed, setIsPropertiesListCollapsed] = useState(false);
    const [localDigitalValues, setLocalDigitalValues] = useState({});
    const {theme} = useTheme()

    // Функция для применения цифрового фильтра с валидацией
    const applyDigitalFilter = useCallback((propertyName, field, value) => {
        const propertyConfig = availableProperties.find(p =>
            propertyName.includes(p.name) // Изменено на includes
        );
        const digitalData = visiblePropertyValues[propertyName]?.data;

        if (!digitalData || !propertyConfig) return;

        let numericValue = value === '' ? undefined : parseFloat(value);

        // Валидация только при применении фильтра
        if (numericValue !== undefined && !isNaN(numericValue)) {
            if (numericValue < digitalData.min) numericValue = digitalData.min;
            if (numericValue > digitalData.max) numericValue = digitalData.max;
        } else {
            numericValue = undefined;
        }

        // Получаем текущий фильтр
        const currentFilter = propertyFilters[propertyName] || {};

        // Создаем обновленный фильтр
        let updatedFilter = {
            ...currentFilter,
            [field]: numericValue
        };

        // Проверка согласованности значений min и max
        if (updatedFilter.min !== undefined && updatedFilter.max !== undefined) {
            if (updatedFilter.min > updatedFilter.max) {
                // Если min больше max, устанавливаем оба значения равными большему из них
                const newValue = field === 'min' ? updatedFilter.min : updatedFilter.max;
                updatedFilter = {
                    min: newValue,
                    max: newValue
                };
            }
        }

        // Обновляем фильтр только если значение изменилось
        if (currentFilter[field] !== updatedFilter[field] ||
            (field === 'min' && currentFilter.max !== updatedFilter.max) ||
            (field === 'max' && currentFilter.min !== updatedFilter.min)) {
            updatePropertyFilter(propertyName, updatedFilter, true);
        }

        // Очищаем только то поле, которое было применено
        setLocalDigitalValues(prev => ({
            ...prev,
            [propertyName]: {
                ...prev[propertyName],
                [field]: undefined // Сбрасываем только примененное поле
            }
        }));
    }, [propertyFilters, availableProperties, visiblePropertyValues, updatePropertyFilter]);

    // Функция для обработки изменения локальных значений (без валидации)
    const handleLocalDigitalChange = useCallback((propertyName, field, value) => {
        setLocalDigitalValues(prev => ({
            ...prev,
            [propertyName]: {
                ...prev[propertyName],
                [field]: value
            }
        }));
    }, []);

    // Функция для обработки нажатия клавиш
    const handleKeyPress = useCallback((e, propertyName, field) => {
        if (e.key === 'Enter') {
            applyDigitalFilter(propertyName, field, e.target.value);
        }
    }, [applyDigitalFilter]);

    const renderDigitalFilter = (propertyName, digitalData) => {
        // Находим конфигурацию свойства для получения unit и step
        const propertyConfig = availableProperties.find(p =>
            propertyName.includes(p.name) // Изменено на includes
        );
        const currentFilter = propertyFilters[propertyName] || {};
        const currentLocal = localDigitalValues[propertyName] || {};

        const step = propertyConfig?.step ? parseFloat(propertyConfig.step) : 5;
        const unit = propertyConfig?.unit || '';

        // Используем displayName если есть, иначе name
        const displayName = propertyConfig?.displayName || propertyName;

        // Получаем значения для отображения (локальные или примененные)
        const displayMin = currentLocal.min !== undefined ? currentLocal.min : (currentFilter.min !== undefined ? currentFilter.min : '');
        const displayMax = currentLocal.max !== undefined ? currentLocal.max : (currentFilter.max !== undefined ? currentFilter.max : '');

        return (
            <div key={propertyName} className="property-filter-group">
                <div className="digital-filter-header">
                    <h4>{displayName}</h4> {/* Используем displayName */}
                </div>

                <div className="digital-filter-inputs">
                    <div className="digital-input-group">
                        <label>От</label>
                        <input
                            type="number"
                            value={displayMin}
                            onChange={(e) => handleLocalDigitalChange(propertyName, 'min', e.target.value)}
                            onBlur={(e) => applyDigitalFilter(propertyName, 'min', e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, propertyName, 'min')}
                            placeholder={`${digitalData.min} ${unit}`}
                            step={step}
                        />
                    </div>

                    <div className="digital-input-group">
                        <label>До</label>
                        <input
                            type="number"
                            value={displayMax}
                            onChange={(e) => handleLocalDigitalChange(propertyName, 'max', e.target.value)}
                            onBlur={(e) => applyDigitalFilter(propertyName, 'max', e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, propertyName, 'max')}
                            placeholder={`${digitalData.max} ${unit}`}
                            step={step}
                        />
                    </div>
                </div>

                {/* Показываем доступные значения для отладки */}
                <div className="available-digital-values">
                    <small>Доступный диапазон: {digitalData.min}{unit} - {digitalData.max}{unit}</small>
                </div>
            </div>
        );
    };

    const renderCheckboxFilter = (propertyName, values) => {
        // Находим конфигурацию свойства для получения displayName
        const propertyConfig = availableProperties.find(p =>
            propertyName.includes(p.name) // Изменено на includes
        );

        // Используем displayName если есть, иначе name
        const displayName = propertyConfig?.displayName || propertyName;

        return (
            <div key={propertyName} className="property-filter-group">
                <h4>{displayName}</h4> {/* Используем displayName */}
                <div className="property-values-list">
                    {values.map(value => (
                        <div key={value} className="filter-checkbox-item"
                             onClick={() => updatePropertyFilter(propertyName, value, !propertyFilters[propertyName]?.includes(value))}>
                            <input
                                type="checkbox"
                                id={`prop-${propertyName}-${value}`}
                                checked={propertyFilters[propertyName]?.includes(value) || false}
                                onChange={(e) => updatePropertyFilter(propertyName, value, e.target.checked)}
                            />
                            <label>
                                {value}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderCategories = (categories) => {
        return categories.map(category => (
            <div
                key={category.path}
                className={`category-button ${category.isCurrent ? 'active' : ''}`}
                style={{ paddingLeft: `${10 + category.level * 20}px` }}
                onClick={() => {
                    handleCategoryNavigation(category.path);
                    onMobileFilterClose();
                }}
            >
                {category.hasChildren && (
                    <span className={`category-arrow ${category.showChildren ? 'expanded' : ''}`}>
                        ▶
                    </span>
                )}
                {category.title}
            </div>
        ));
    };

    return (
        <div
            className={`filters-sidebar ${theme} ${isMobileFilterOpen ? 'mobile-open' : ''} ${showFilterPanel ? "active" : ""}`}
            onClick={(e) => {
                if (!isMobileFilterOpen && e.target.closest('.mobile-compact-header')) {
                    onMobileFilterToggle();
                }
            }}
        >
            {/* Компактный заголовок для мобильных */}
            <div className="mobile-compact-header">
                <h3 className="filter-title-text">
                    <FilterOutlined className="filter-icon" />
                    Фильтр
                </h3>
                {
                    isMobileFilterOpen ?
                        <button
                            className="mobile-close-btn"
                            onClick={onMobileFilterClose}
                            aria-label="Закрыть фильтр"
                        >
                            ×
                        </button> :
                        <span className={`toggle-icon open`}>
                            ▼
                        </span>
                }
            </div>

            <div className="filters-content">
                <h3 className="filter-title">
                    <FilterOutlined className="filter-icon" />
                    Фильтр
                </h3>

                {showFilterPanel && visibleContextCategories.length > 0 && (
                    <div className="category-filter-section">
                        <h4>Категория</h4>
                        <div className="category-buttons">
                            {renderCategories(visibleContextCategories)}
                        </div>
                    </div>
                )}

                {allTags.length > 0 && (
                <div className="tags-filter-section">
                    <div
                        className="tags-filter-header"
                        onClick={() => setIsTagsListCollapsed(!isTagsListCollapsed)}
                    >
                        <span className={`collapse-arrow ${isTagsListCollapsed ? 'collapsed' : ''}`}>
                            ▼
                        </span>
                        <h4>Фильтры по контейнер-цистернам</h4>
                    </div>

                    <div className={`tags-filter-list ${isTagsListCollapsed ? "" : "open"}`}>
                        <div className="tags-filter-list-inner">
                            {allTags.map(tag => (
                                <div key={tag.key} className="filter-checkbox-item"
                                     onClick={() => toggleTagFilter(tag.key)}>
                                    <input
                                        type="checkbox"
                                        id={`tag-${tag.key}`}
                                        checked={selectedTags.includes(tag.key)}
                                        onChange={() => toggleTagFilter(tag.key)}
                                    />
                                    <label htmlFor={`tag-${tag.key}`}>
                                        {tag.name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                )}

                {availableProperties.length > 0 && (
                    <div className="properties-filter-section">
                        <div
                            className="properties-filter-header"
                            onClick={() => setIsPropertiesListCollapsed(!isPropertiesListCollapsed)}
                        >
                            <span className={`collapse-arrow ${isPropertiesListCollapsed ? 'collapsed' : ''}`}>
                                ▼
                            </span>
                            <h4>Фильтры по свойствам</h4>
                        </div>

                        <div className={`properties-filter-list ${isPropertiesListCollapsed ? "" : "open"}`}>
                            {availableProperties.map(propertyConfig => {
                                const propertyName = propertyConfig.name;
                                const propertyData = visiblePropertyValues[propertyName];

                                if (!propertyData || !propertyData.data) return null;

                                if (propertyConfig.type === FILTER_TYPES.DIGITAL) {
                                    return renderDigitalFilter(propertyName, propertyData.data);
                                } else {
                                    if (propertyData.data.length === 0) return null;
                                    return renderCheckboxFilter(propertyName, propertyData.data);
                                }
                            })}
                        </div>
                    </div>
                )}

                {(selectedTags.length > 0 || Object.keys(propertyFilters).length > 0) && (
                    <div className="reset-buttons">
                        <button
                            onClick={() => {
                                setSelectedTags([]);
                                resetPropertyFilters();
                                setLocalDigitalValues({});
                            }}
                            className="reset-filter-button"
                        >
                            Сбросить все фильтры
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}